import { useNavigate } from 'react-router-dom';
import {
  Page,
  Card,
  Text,
  Button,
  Badge,
  Banner,
  ProgressBar,
  BlockStack,
  InlineStack,
  List,
  Box,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { client } from '../api/client';
import { Endpoints } from '../api/endpoints';

interface Step {
  id: number;
  key: string;
  title: string;
  description: string;
  guide: string[];
  done: boolean;
  blocking: boolean;
  action_label: string;
  action_url: string;
  stats: Record<string, number> | null;
}

interface OnboardingStatus {
  shop_name: string;
  plan: string;
  completed: number;
  total_steps: number;
  progress: number;
  current_step: number;
  dismissed: boolean;
  steps: Step[];
}

const STEP_EMOJI = ['📦', '🏷️', '🧾', '📣'];

export default function Onboarding() {
  const navigate = useNavigate();
  const { data, loading } = useApi<OnboardingStatus>(Endpoints.onboarding.status);

  const dismiss = async () => {
    await client.post(Endpoints.onboarding.dismiss);
    navigate('/');
  };

  const allDone = data ? data.completed === data.total_steps : false;

  return (
    <Page
      title="Getting started"
      subtitle="Set up ProfitVault in a few minutes"
      secondaryActions={[{ content: 'Skip for now', onAction: dismiss }]}
    >
      <BlockStack gap="400">
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h3" variant="headingMd">
                {allDone ? '🎉 You’re all set!' : 'Setup progress'}
              </Text>
              <Text as="span" variant="bodyMd" tone="subdued">
                {data ? `${data.completed}/${data.total_steps} done` : '…'}
              </Text>
            </InlineStack>
            <ProgressBar
              progress={data?.progress ?? 0}
              tone={allDone ? 'success' : 'primary'}
            />
            {allDone && (
              <Banner tone="success">
                Everything’s configured. Head to your dashboard to see real profit.
                <Box paddingBlockStart="200">
                  <Button variant="primary" onClick={dismiss}>
                    Go to dashboard
                  </Button>
                </Box>
              </Banner>
            )}
          </BlockStack>
        </Card>

        {(data?.steps ?? []).map((step, i) => (
          <Card key={step.key}>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="300" blockAlign="center">
                  <Text as="span" variant="headingLg">
                    {STEP_EMOJI[i] ?? '•'}
                  </Text>
                  <BlockStack gap="0">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="h3" variant="headingMd">
                        {step.id}. {step.title}
                      </Text>
                      {step.done ? (
                        <Badge tone="success">Done</Badge>
                      ) : step.blocking ? (
                        <Badge tone="attention">To do</Badge>
                      ) : (
                        <Badge>Optional</Badge>
                      )}
                    </InlineStack>
                    <Text as="span" tone="subdued" variant="bodySm">
                      {step.description}
                    </Text>
                  </BlockStack>
                </InlineStack>
                <Button
                  variant={step.done ? 'secondary' : 'primary'}
                  onClick={() => navigate(step.action_url)}
                >
                  {step.done ? 'Review' : step.action_label}
                </Button>
              </InlineStack>

              {!step.done && (
                <Box paddingInlineStart="800">
                  <List type="number">
                    {step.guide.map((g, gi) => (
                      <List.Item key={gi}>{g}</List.Item>
                    ))}
                  </List>
                </Box>
              )}

              {step.stats && (
                <Box paddingInlineStart="800">
                  <Text as="span" variant="bodySm" tone="subdued">
                    {Object.entries(step.stats)
                      .map(([k, v]) => `${v} ${k.replace(/_/g, ' ')}`)
                      .join(' · ')}
                  </Text>
                </Box>
              )}
            </BlockStack>
          </Card>
        ))}

        {!loading && !allDone && (
          <InlineStack align="center">
            <Button variant="plain" onClick={dismiss}>
              I’ll finish later
            </Button>
          </InlineStack>
        )}
      </BlockStack>
    </Page>
  );
}
